import {
  MouseEvent,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import { Text } from '@hhgtech/hhg-components/components'
import { BoxProps } from '@mantine/core'
import { useIntl } from 'react-intl'
import { AudioPlayerProvider } from 'react-use-audio-player'

import { redirectToSSOWithReturnUrl } from 'api/utils/redirects'
import { Loading } from 'components/atoms/loading'
import { useIsMobile } from 'hooks/useIsMobile'
import { useRouterExtend } from 'hooks/useRouterExtend'
import { LOCALE } from 'interfaces/types'
import { AppContext } from 'state/appContext'

import AudioSidebar from './audioSidebar'
import { AudioPlayerControls } from './controls'
import {
  StyledAudioPlayerMiddle,
  StyledAudioPlayerWrapper,
} from './index.styled'
import { Player } from './player'
import { AudioTrackProgress } from './progress'
import { ArticleAudioPlayerState } from './types'

type Props = {
  articleId?: string | number
  articleContent?: string
  isPremium?: boolean
  isSidebar?: boolean
  onError?: (ref?: HTMLDivElement | null) => void
  onLoading?: (isLoading: boolean, ref?: HTMLDivElement | null) => void
} & BoxProps

const ArticleAudioPlayerInner = ({
  articleId = '0',
  articleContent = '',
  isPremium,
  onError,
  onLoading,
  isSidebar,
  ...rest
}: Props) => {
  const { formatMessage: f } = useIntl()
  const isMobile = useIsMobile()

  const {
    state: { isLoggedIn },
  } = useContext(AppContext)
  const [
    audioPlayerState,
    setAudioPlayerState,
  ] = useState<ArticleAudioPlayerState>('loading')
  const ref = useRef<HTMLDivElement>(null)
  const [audioFileUrl, setAudioFileUrl] = useState('')
  const [playAudioFileUrl, setPlayAudioFileUrl] = useState('')
  const [postContentLength, setPostContentLength] = useState(0)
  const isLoadingAudio = ['generating', 'loading'].includes(audioPlayerState)

  useEffect(() => {
    audioPlayerState === 'error' && onError?.(ref.current)
    isLoadingAudio && onLoading?.(!isLoadingAudio, ref.current)
  }, [audioPlayerState])

  useEffect(() => {
    const el = document.createElement('div')
    el.innerHTML = articleContent
    const textContent = el.textContent?.trim().replace(/\[.*\]/g, '') || ''
    if (!textContent.length) {
      console.debug(
        `[ArticleAudioPlayer] hidden because of empty article content`,
      )
    }
    setPostContentLength(textContent.length)
  }, [articleContent])
  useEffect(() => {
    fetch(
      `/api/read-article/${articleId}?t=${
        // every 15 min
        Math.floor(Date.now() / 30 / 60 / 1000)
      }`,
    )
      .then((r) => r.json())
      .then((data) => {
        if (data.url) {
          setAudioFileUrl(data.url)
          setAudioPlayerState('idle')
        } else {
          setAudioPlayerState('error')
        }
      })
    /**
     * Test audio on local
    const data = {
      success: true,
      url: 'https://staging-audio.hellohealthgroup.com/464786-vi-VN-f.mp3',
    }
    setAudioFileUrl(data.url)
    setAudioPlayerState('idle')
     */
  }, [articleId])

  if (postContentLength === 0) {
    return <StyledAudioPlayerWrapper />
  }

  const handleArticleClick = (e: MouseEvent<HTMLDivElement>) => {
    if (isPremium && !isLoggedIn) {
      redirectToSSOWithReturnUrl(window.location.href)
      e.stopPropagation()
    }
  }

  const onClickPlay = () => {
    setPlayAudioFileUrl(audioFileUrl)
  }

  if (isSidebar) {
    return (
      <AudioPlayerProvider>
        <AudioSidebar
          articleId={Number(articleId)}
          onClickPlay={onClickPlay}
          audioPlayerState={audioPlayerState}
        />
        {playAudioFileUrl && (
          <Player
            articleId={Number(articleId)}
            setAudioPlayerState={setAudioPlayerState}
            url={playAudioFileUrl}
          />
        )}
      </AudioPlayerProvider>
    )
  }

  return (
    <AudioPlayerProvider>
      <StyledAudioPlayerWrapper
        ref={ref}
        onClickCapture={handleArticleClick}
        {...rest}
      >
        {!isLoadingAudio && audioPlayerState !== 'error' && (
          <AudioPlayerControls
            articleId={Number(articleId)}
            audioPlayerState={audioPlayerState}
            onClickPlay={onClickPlay}
          />
        )}
        {audioPlayerState === 'error' && (
          <Text size={isMobile ? 'xs' : 'p3'}>
            {f({
              id: 'player.error',
              defaultMessage: 'Error reading article',
            })}
          </Text>
        )}
        <StyledAudioPlayerMiddle>
          {isLoadingAudio && <Loading />}
          {playAudioFileUrl && (
            <Player
              articleId={Number(articleId)}
              setAudioPlayerState={setAudioPlayerState}
              url={playAudioFileUrl}
            />
          )}
          {audioPlayerState === 'idle' && (
            <Text
              style={{
                whiteSpace: 'nowrap',
                textOverflow: 'ellipsis',
                overflow: 'hidden',
              }}
              size={isMobile ? 'p4' : 'p3'}
              className="mobile-navigation-label"
            >
              {f({
                id: 'player.readArticle',
                defaultMessage: 'Read Article',
              })}
            </Text>
          )}
          {['playing', 'paused', 'ended'].includes(audioPlayerState) && (
            <AudioTrackProgress articleId={Number(articleId)} />
          )}
        </StyledAudioPlayerMiddle>
      </StyledAudioPlayerWrapper>
    </AudioPlayerProvider>
  )
}

export function ArticleAudioPlayer({
  articleEnableAudio,
  articleTurnOffArticleVoice,
  ...props
}: Props & {
  articleTurnOffArticleVoice?: boolean
  articleEnableAudio?: boolean
}) {
  const { locale } = useRouterExtend()
  const turnOffArticleVoice = articleTurnOffArticleVoice || !articleEnableAudio
  const isAudioPlayerVisible = useMemo(() => {
    return (
      [LOCALE.Vietnam, LOCALE.Indonesia].includes(locale) &&
      !turnOffArticleVoice
    )
  }, [locale, turnOffArticleVoice])

  if (!isAudioPlayerVisible) return null

  return <ArticleAudioPlayerInner key={props.articleId} {...props} />
}
